import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CopyIcon, ListItem } from "@activecollab/components";
import PropTypes from "prop-types";
import MoveCopyToProjectDialog from "~/components/MoveCopyToProject/MoveCopyToProjectDialog";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import * as actionTypes from "~/store/action_types";
import { moveCopyNoteToProject } from "~/store/actions/actions.notes";
import { showErrorToast, showSuccessToast } from "../Toast";

export const NoteMoveCopyToProject = ({ noteId, projectId, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: sheetOpen, closeSheet } = useObjectSheetContext();

  const [open, setOpen] = useState(false);

  const handleSubmit = useCallback(
    (attributes) => {
      setOpen(false);
      const { project_id, copy = false, redirect = true } = attributes;
      dispatch(
        moveCopyNoteToProject(projectId, noteId, {
          project_id,
          copy,
        })
      )
        .then((action) => {
          const { newProjectId = 0, response = {}, type } = action;
          const { result = {} } = response;

          if (typeof onSuccess === "function") {
            onSuccess();
          }

          if (type === actionTypes.MOVE_NOTE_TO_PROJECT_SUCCESS) {
            showSuccessToast(copy ? t("Note copied to project") : t("Note moved to project"));
          } else {
            showErrorToast(copy ? t("Failed to copy note") : t("Failed to move note"));
          }
          const { single = 0 } = result;
          if (newProjectId > 0 && single > 0) {
            if (redirect) {
              navigate(`/projects/${newProjectId}/notes/${single}`);
            } else if (sheetOpen) {
              closeSheet();
            }
          }
        })
        .catch(() => null);
    },
    [dispatch, projectId, noteId, onSuccess, t, sheetOpen, navigate, closeSheet]
  );

  const excludeIds = useMemo(() => [projectId], [projectId]);

  const handleOpen = () => setOpen(true);
  const handleCancel = () => setOpen(false);

  return (
    <>
      <ListItem onClick={handleOpen}>
        <CopyIcon className="tw-fill-theme-900 tw-shrink-0" />
        {t("Move/Copy to Project")}
      </ListItem>
      <MoveCopyToProjectDialog
        open={open}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        excludeIds={excludeIds}
        type="Note"
      />
    </>
  );
};

NoteMoveCopyToProject.propTypes = {
  open: PropTypes.bool,
  projectId: PropTypes.number.isRequired,
  noteId: PropTypes.number.isRequired,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
};

NoteMoveCopyToProject.defaultProps = {
  open: false,
};
